import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { ChunkLoadErrorHandler } from './_core/chunk-load-error.handler';
import { provideInstantPageScroll } from './_core/page-change-scroll';
import { provideNoInstallPrompt } from './_core/no-install-prompt';

import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { HttpInterceptor } from '@lib-common/interceptors/http-token.interceptor';
import { NOTIFICATION_HANDLER } from '@lib-common/services/notification-handler.token';
import { AuthenticationService } from '@lib-common/services/authentication.service';
import { ENVIRONMENT } from '@lib-common/types/environment.types';
import { environment } from '../environments/environments';
import { ToastService } from './_core/toast.service';
import { ContentsquareService } from './consent/contentsquare.service';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    provideZonelessChangeDetection(),
    // ⚠ `withFetch()` added by Story 78.1 and it is REQUIRED, not advisory.
    // Angular's non-fetch HttpClient falls back to an XHR shim on the server
    // that this app does not ship, so every server-side request failed with
    // `statusCode: 0` — a server-rendered page whose data silently never
    // arrived, rendering its error state into the HTML a crawler reads.
    // Angular logs NG02801 about this at boot. Safe in the browser too: fetch
    // is the modern default, and this app requests no upload progress events
    // (no `reportProgress` / `HttpEventType` usage anywhere), which is the one
    // capability the fetch backend does not provide.
    provideHttpClient(withFetch(), withInterceptors([HttpInterceptor])),
    provideOAuthClient(),
    provideClientHydration(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    // Story 86.21 (Endi 2026-10-25): a page change (another path, no fragment, not `scroll: 'manual'`; Back and Forward
    // included) scrolls the next page to its top at once, so a Back pressed soon after lands exactly where the visitor
    // was. Query-only, fragment and same-URL navigations scroll as before. Browser only.
    provideInstantPageScroll(),
    // Story 87.27 (D5): no install prompt UI, the browser's mini-infobar included. Browser only.
    provideNoInstallPrompt(),
    { provide: ENVIRONMENT, useValue: environment },
    // Lets lib-common's HttpService surface backend 413/415 upload
    // rejections via the same toast pipeline used by the cropper's
    // client-side guard.
    { provide: NOTIFICATION_HANDLER, useExisting: ToastService },
    // Bug 1 (`feature/sprint-5-follow-up-fixes`): block app render until session
    // restoration resolves. Without this, reload silently logs the user out —
    // page content rendered before `getUserOnLoadZitadel()` (fire-and-forget
    // from the constructor) could populate `currentUser`. `bootstrapSession()`
    // never rejects, so a Zitadel outage falls through to the logged-out UI.
    provideAppInitializer(() => inject(AuthenticationService).bootstrapSession()),
    // Story 50.6 — consent-gated Contentsquare. Started before the initial navigation so it sees the
    // URL the document was loaded with; a no-op on the server and in builds without a tag ID.
    provideAppInitializer(() => inject(ContentsquareService).start()),
  ],
};
